import { sliderToMonthRange } from "../../../shared/utils/childAge";

const DEPARTMENT_MAP: Record<number, string> = {
  1: "pediatrician",
  2: "obstetrician",
  3: "midwife",
};

export interface SearchKeys {
  department_types: number[];
  icd10_keyword: string;
  doctor_family_name: string;
  doctor_first_name: string;
  answer_keywords: string[];
  child_genders: number[];
  child_age_range: object;
  child_age_slider: number[];
  doctor_id: number | null;
  only_my_answer: boolean;
  exclude_mode: "without_exclude" | "only_exclude" | "with_exclude";
}

export interface BuildSearchKeysParams {
  departmentTypes: number[];
  icd10Keyword: string;
  doctorFamilyName: string;
  doctorFirstName: string;
  splitKeywords: string[];
  hasPediatricDepartment: boolean;
  childGenders: number[];
  childAgeSlider: number[];
  doctorId: number | null;
  isOperatorDoctor: boolean;
  excludeMode: "without_exclude" | "only_exclude" | "with_exclude";
}

export interface AnsweredSearchRequestQuery {
  departmentTypes: string[];
  doctorFamilyName: string;
  doctorFirstName: string;
  icd10Keyword: string;
  answerKeywords: string;
  answerKeywordScopes: string[];
  childSex: number[];
  childAgeRange: object;
  exclude_mode: "without_exclude" | "only_exclude" | "with_exclude";
  sortRules: Array<{ column: string; direction: string }>;
  doctorId: number | null;
  userInterest: unknown[];
}

function mapDepartmentTypes(departmentTypes: number[] = []): string[] {
  return departmentTypes
    .map((type) => DEPARTMENT_MAP[Number(type)])
    .filter(Boolean);
}

export function buildSearchKeys(params: BuildSearchKeysParams): SearchKeys {
  return {
    department_types: params.departmentTypes,
    icd10_keyword: params.icd10Keyword,
    doctor_family_name: params.doctorFamilyName,
    doctor_first_name: params.doctorFirstName,
    answer_keywords: params.splitKeywords,
    child_genders: params.hasPediatricDepartment ? params.childGenders : [],
    child_age_range: params.hasPediatricDepartment ? sliderToMonthRange(params.childAgeSlider) : {},
    child_age_slider: params.hasPediatricDepartment ? [...params.childAgeSlider] : [0, 35],
    doctor_id: params.doctorId,
    only_my_answer: params.doctorId != null,
    exclude_mode: params.isOperatorDoctor ? params.excludeMode : "without_exclude",
  };
}

export function buildAnsweredSearchRequestQuery(searchKeys: SearchKeys): AnsweredSearchRequestQuery {
  return {
    departmentTypes: mapDepartmentTypes(searchKeys.department_types || []),
    doctorFamilyName: searchKeys.doctor_family_name || "",
    doctorFirstName: searchKeys.doctor_first_name || "",
    icd10Keyword: searchKeys.icd10_keyword || "",
    answerKeywords: (searchKeys.answer_keywords || []).join(" "),
    answerKeywordScopes: ["answer"],
    childSex: searchKeys.child_genders || [],
    childAgeRange: searchKeys.child_age_range || {},
    exclude_mode: searchKeys.exclude_mode || "without_exclude",
    sortRules: [{ column: "score", direction: "desc" }],
    doctorId: searchKeys.doctor_id || null,
    userInterest: []
  };
}

export function buildBulkExcludeQuery(searchKeys: SearchKeys | null) {
  if (!searchKeys?.department_types?.length) return null;

  const departmentTypes = searchKeys.department_types
    .map((type) => DEPARTMENT_MAP[Number(type)])
    .filter(Boolean);

  return {
    departmentTypes,
    doctorFamilyName: searchKeys.doctor_family_name || "",
    doctorFirstName: searchKeys.doctor_first_name || "",
    icd10Keyword: searchKeys.icd10_keyword || "",
    answerKeywords: (searchKeys.answer_keywords || []).join(" "),
    answerKeywordScopes: ["answer"],
    childSex: searchKeys.child_genders || [],
    childAgeRange: searchKeys.child_age_range || {},
    inPregnancy: [],
    userAgeRank: [],
    weeksOfPregnancyRank: [],
    monthsOfPostpartumRank: [],
    userInterest: [],
    sortRules: [{ column: "id", direction: "desc" }],
    displayScore: false,
    doctorId: searchKeys.doctor_id || null,
  };
}

export function normalizeChildAgeSlider(sliderValue: unknown): [number, number] {
  if (!sliderValue || !Array.isArray(sliderValue)) return [0, 35];
  const sorted = [...sliderValue].map(Number).sort((a, b) => a - b);
  return [Math.max(0, sorted[0]), Math.min(35, sorted[sorted.length - 1])];
}
